import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { authedFetch } from './utils/api';
import { color, naira, radius, space } from './ui/theme';
import { Banner, Btn } from './ui/kit';

/**
 * The customer book, with the records that look like one customer grouped.
 *
 * NOTHING IS MERGED AUTOMATICALLY, and the screen is built around that. Two
 * records on one phone number may be a husband and wife, a hospital and the
 * nurse who orders for it, or a shared office line. The system is confident
 * enough to ask and never confident enough to decide, so every merge is a
 * person picking a survivor, ticking the records to absorb, and typing why.
 *
 * A merge moves order history, invoices, payments, previous debts and twelve
 * other tables onto the survivor. It cannot be cleanly undone, which is why
 * the absorbed records are listed by name with their order counts and value
 * before the button will do anything.
 */

async function req(url, opts) {
  const res = await authedFetch(url, opts);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.detail || `Request failed (${res.status})`);
  return body;
}

const inputStyle = {
  width: '100%', padding: '9px 11px', borderRadius: radius.sm,
  border: `1px solid ${color.border}`, fontSize: 13.5,
};
const th = { textAlign: 'left', padding: '8px 10px', fontSize: 11.5,
             color: color.textSecondary, fontWeight: 600,
             borderBottom: `1px solid ${color.border}` };
const td = { padding: '8px 10px', fontSize: 13,
             borderBottom: `1px solid ${color.borderLight || color.border}` };

function Group({ group, onMerged, notify }) {
  const [survivor, setSurvivor] = useState(group.suggested_survivor_id);
  const [absorb, setAbsorb] = useState(() => new Set());
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  // The survivor can never also be absorbed; the service refuses it, and
  // offering the tick at all invites the mistake.
  useEffect(() => { setAbsorb(prev => {
    const next = new Set(prev); next.delete(survivor); return next;
  }); }, [survivor]);

  const toggle = (id) => setAbsorb(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const chosen = group.members.filter(m => absorb.has(m.customer_id));
  const keeper = group.members.find(m => m.customer_id === survivor);
  const movingOrders = chosen.reduce((n, m) => n + m.orders, 0);
  const movingValue = chosen.reduce((n, m) => n + m.value, 0);

  const go = async () => {
    if (!chosen.length) { notify?.('Tick the records to merge in', 'error'); return; }
    if (reason.trim().length < 3) {
      notify?.('Say why these are the same customer', 'error'); return;
    }
    setBusy(true);
    try {
      await req('/api/customers/merge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          surviving_id: survivor,
          merged_ids: chosen.map(m => m.customer_id),
          reason: reason.trim(),
        }),
      });
      notify?.(
        `${chosen.length} record${chosen.length > 1 ? 's' : ''} merged into `
        + `${keeper?.name}.`, 'success');
      onMerged?.();
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ border: `1px solid ${color.border}`, borderRadius: radius.md,
                  marginBottom: space(2), overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', background: color.surfaceAlt || '#f7f7f9',
                    borderBottom: `1px solid ${color.border}` }}>
        <strong style={{ fontSize: 13.5 }}>{group.members.length} records</strong>
        <span style={{ color: color.textSecondary, fontSize: 12.5 }}>
          {' '}&middot; {group.reasons.join(', ')} &middot; {group.total_orders} orders between them
        </span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>Keep</th><th style={th}>Merge in</th>
            <th style={th}>Name</th><th style={th}>Phone</th>
            <th style={{ ...th, textAlign: 'right' }}>Orders</th>
            <th style={{ ...th, textAlign: 'right' }}>Value</th>
            <th style={{ ...th, textAlign: 'right' }}>Invoices</th>
          </tr></thead>
          <tbody>
            {group.members.map(m => {
              const isKeeper = m.customer_id === survivor;
              return (
                <tr key={m.customer_id}
                    style={{ background: isKeeper ? (color.successBg || '#f0fbf4')
                                                  : 'transparent' }}>
                  <td style={td}>
                    <input type="radio" name={`keep-${group.members[0].customer_id}`}
                           checked={isKeeper}
                           onChange={() => setSurvivor(m.customer_id)} />
                  </td>
                  <td style={td}>
                    <input type="checkbox" disabled={isKeeper}
                           checked={absorb.has(m.customer_id)}
                           onChange={() => toggle(m.customer_id)} />
                  </td>
                  <td style={td}>
                    {m.name}
                    {m.customer_code && (
                      <div style={{ fontSize: 11, color: color.textMuted }}>
                        {m.customer_code}
                      </div>)}
                  </td>
                  <td style={td}>{m.phone || '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{m.orders}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{naira(m.value)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{m.invoices}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ padding: space(1.5) }}>
        {chosen.length > 0 && (
          <Banner tone="warning" title="This cannot be undone">
            {movingOrders} order{movingOrders === 1 ? '' : 's'} worth{' '}
            {naira(movingValue)} will move onto <strong>{keeper?.name}</strong>.
            {' '}{chosen.map(m => m.name).join(', ')} will no longer appear in
            the customer book.
          </Banner>
        )}
        <div style={{ marginTop: space(1) }}>
          <div style={{ fontSize: 11.5, color: color.textSecondary,
                        marginBottom: 4 }}>
            Why are these the same customer? (kept on the record)
          </div>
          <input style={inputStyle} value={reason}
                 placeholder="e.g. same person, name typed two ways"
                 onChange={(e) => setReason(e.target.value)} />
        </div>
        <div style={{ marginTop: space(1.5) }}>
          <Btn disabled={busy || !chosen.length} onClick={go}>
            {busy ? 'Merging…'
              : chosen.length
                ? `Merge ${chosen.length} into ${keeper?.name}`
                : 'Tick the records to merge in'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

export default function CustomerMerge({ notify }) {
  const [data, setData] = useState(null);
  const [all, setAll] = useState([]);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('groups');

  const load = useCallback(async () => {
    setError(null);
    try {
      // limit=1000: the list defaults to 20 a page, and a deduplication
      // screen that silently shows the first twenty customers is worse than
      // no screen. active_only=false because a record somebody deactivated
      // instead of merging is exactly what this is here to find.
      const [dupes, customers] = await Promise.all([
        req('/api/customers/duplicates'),
        req('/api/sales/customers?limit=1000&active_only=false'),
      ]);
      setData(dupes);
      setAll(customers.items || []);
      if (customers.total > (customers.items || []).length) {
        setError(`Showing ${(customers.items || []).length} of `
                 + `${customers.total} customers.`);
      }
    } catch (e) { setError(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Which records the detector has already flagged, so the full book can mark
  // them rather than making somebody cross-reference two lists by eye.
  const flagged = useMemo(() => {
    const s = new Set();
    (data?.groups || []).forEach(g =>
      g.members.forEach(m => s.add(m.customer_id)));
    return s;
  }, [data]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(c =>
      `${c.name || ''} ${c.phone || ''} ${c.customer_code || ''}`
        .toLowerCase().includes(q));
  }, [all, search]);

  return (
    <div>
      {error && <Banner tone="danger" title="Could not load">{error}</Banner>}

      <div style={{ display: 'flex', gap: 8, marginBottom: space(2) }}>
        {[['groups', `Possible duplicates${data ? ` (${data.group_count})` : ''}`],
          ['all', `All customers${all.length ? ` (${all.length})` : ''}`]
        ].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
                  style={{ padding: '7px 14px', borderRadius: radius.sm,
                           fontSize: 13, cursor: 'pointer',
                           border: `1px solid ${tab === k ? color.royal : color.border}`,
                           background: tab === k ? color.royal : 'transparent',
                           color: tab === k ? '#fff' : color.text }}>
            {label}
          </button>
        ))}
        <Btn size="sm" variant="secondary" onClick={load}>Refresh</Btn>
      </div>

      {tab === 'groups' && data && (
        <>
          <Banner tone="info" title="Nothing is merged automatically">
            Two records sharing a phone number may be one customer typed twice,
            or they may be a hospital and the nurse who orders for it. Check
            each group before merging: a merge moves order history onto the
            survivor and cannot be cleanly undone.
          </Banner>
          {data.groups.length === 0
            ? <p style={{ color: color.textSecondary }}>
                No duplicates found. This list is computed fresh each time, so
                a group disappears once it is merged.
              </p>
            : data.groups.map((g, i) => (
                <Group key={g.members[0].customer_id + i} group={g}
                       notify={notify} onMerged={load} />
              ))}
        </>
      )}

      {tab === 'all' && (
        <>
          <input style={{ ...inputStyle, maxWidth: 360, marginBottom: space(1.5) }}
                 value={search} placeholder="Search name, phone or code"
                 onChange={(e) => setSearch(e.target.value)} />
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={th}>#</th><th style={th}>Name</th>
                <th style={th}>Phone</th><th style={th}>Code</th>
                <th style={th}>Registered by</th><th style={th}>Flagged</th>
              </tr></thead>
              <tbody>
                {shown.map((c, i) => (
                  <tr key={c.id}>
                    <td style={{ ...td, color: color.textMuted }}>{i + 1}</td>
                    <td style={td}>{c.name}</td>
                    <td style={td}>{c.phone || '—'}</td>
                    <td style={td}>{c.customer_code || '—'}</td>
                    {/* Never guessed. A customer added before this was
                        captured has no honest answer, and inventing one from
                        who raised their first order would read like a record
                        without being one. */}
                    <td style={td}>
                      {c.created_by_name || (
                        <span style={{ color: color.textMuted, fontSize: 12 }}>
                          not recorded
                        </span>)}
                    </td>
                    <td style={td}>
                      {flagged.has(String(c.id)) && (
                        <span style={{ background: color.warningBg || '#fff4e5',
                                       color: color.warning || '#b26a00',
                                       padding: '2px 8px', borderRadius: 10,
                                       fontSize: 11, fontWeight: 600 }}>
                          possible duplicate
                        </span>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {shown.length === 0 && (
            <p style={{ color: color.textSecondary }}>No customer matches that.</p>
          )}
        </>
      )}
    </div>
  );
}
